import { useRef, useState } from 'react'
import Link from 'next/link'
import { Swiper, SwiperSlide } from 'swiper/react'
import cn from 'classnames'
// Components
import PuppyCard from '../newPuppyCard'
import styles from './RelatedBreeds.module.scss'
// Utils
import { isFavourite } from '@/components/search/lib/utils'
import { stateMap, useMediaQuery } from '@utils'

const RelatedBreedsStrip = ({ breed, state, favourites, updateFavourite }) => {
  const isMobile = useMediaQuery('(max-width:600px)')
  /* Swiper Handlers   */
  const ref = useRef(null)
  const [limit, setLimit] = useState(isMobile ? 3 : 5)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const ROOT_URL = process.env.NEXT_PUBLIC_ROOT_URL

  const goNext = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slideNext()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
    }
  }

  const goPrev = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slidePrev()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
    }
  }

  const converterState = (slug) => {
    for (const state in stateMap) {
      if (stateMap[state].state === slug) {
        return stateMap[state].slug
      }
    }
    return slug
  }

  return (
    <div className={styles.relatedBreed} id={`breed-${breed.id}`}>
      <div className={styles.header}>
        <Link
          href={
            state
              ? `${ROOT_URL}/puppies-for-sale/${breed.slug}/${converterState(breed?.dogs[0]?.breeder.state)}`
              : `${ROOT_URL}/puppies-for-sale/${breed.slug}`
          }
        >
          <h3 className={styles.name}>
            {breed?.name}
            {breed.dogs?.length < 5 && (
              <span>
                {' '}
                ({breed.dogsAvailable || breed.breed.dogsAvailable} puppies
                left)
              </span>
            )}
          </h3>
        </Link>

        {breed.dogs?.length > 4 && (
          <>
            <button
              className={cn({
                [styles.swiperButton]: true,
                [styles.swiperPrev]: !isBeginning,
                [styles.hidePrev]: isBeginning,
              })}
              onClick={goPrev}
              role="img"
              aria-label="Button Prev"
            />
            <button
              className={cn({
                [styles.swiperButton]: true,
                [styles.swiperNext]: !isEnd,
                [styles.hideNext]: isEnd,
              })}
              onClick={() => {
                goNext()
                setLimit(limit + 1)
              }}
              role="img"
              aria-label="Button Next"
            />
            <Link
              legacyBehavior
              href={
                state
                  ? `${ROOT_URL}/puppies-for-sale/${breed.slug}/${converterState(breed?.dogs[0]?.breeder.state)}`
                  : `${ROOT_URL}/puppies-for-sale/${breed.slug}`
              }
            >
              <a className={styles.viewAll}> View All</a>
            </Link>
          </>
        )}
      </div>

      <div className={styles.cards}>
        <Swiper
          speed={1000}
          initialSlide={0}
          grabCursor
          breakpoints={{
            1020: {
              slidesPerView: 4,
              spaceBetween: 20,
            },
            860: {
              slidesPerView: 3,
              spaceBetween: 40,
            },
            540: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            360: {
              slidesPerView: 'auto',
              spaceBetween: 10,
            },
          }}
          onSlideChange={() => {
            setLimit(limit + 1)
          }}
          ref={ref}
        >
          {breed?.dogs?.slice(0, limit).map((d, i) => (
            <SwiperSlide key={i}>
              <PuppyCard
                dog={d}
                state={state}
                isFavourite={isFavourite(favourites, d.id)}
                updateFavourite={() => updateFavourite(d.id)}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  )
}

export default function RelatedBreeds({
  title,
  relatedBreeds,
  state,
  favourites,
  updateFavourite,
}) {
  return (
    <div className={styles.relatedBreeds} id="related-breeds">
      <h2>{title}</h2>

      {relatedBreeds?.map((breed, i) => (
        <RelatedBreedsStrip
          key={i}
          breed={breed}
          state={state}
          updateFavourite={updateFavourite}
          favourites={favourites}
        />
      ))}
    </div>
  )
}
